import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface SuccessPageState {
  email: string;
  resources: Array<{
    id: number;
    title: string;
    description: string;
    category: string;
    downloadToken?: string;
    downloadUrl?: string;
  }>;
}

interface WidgetSettings {
  school_name?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  logo_url?: string;
}

const EnrollSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as SuccessPageState | null;
  const [settings, setSettings] = useState<WidgetSettings>({
    school_name: 'My Academy',
    primary_color: '#4F46E5',
    secondary_color: '#7C3AED',
    accent_color: '#10B981'
  });

  // Extract form type from query parameter for GA4 tracking
  const queryParams = new URLSearchParams(location.search);
  const formType = queryParams.get('type'); // 'newsletter' or 'contact'

  // Log for debugging
  useEffect(() => {
    if (formType) {
      console.log(`✅ GA4 Tracking: Form type = ${formType}`);
    }
  }, [formType]);

  // Redirect to form if accessed directly without state
  useEffect(() => {
    if (!state || !state.email) {
      navigate('/contact', { replace: true });
    }
  }, [state, navigate]);

  // Fetch widget settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${API_URL}/widget/config`);
        if (response.ok) {
          const data = await response.json();
          setSettings({
            school_name: data.school_name || 'My Academy',
            primary_color: data.primary_color || '#4F46E5',
            secondary_color: data.secondary_color || '#7C3AED',
            accent_color: data.accent_color || '#10B981',
            logo_url: data.logo_url
          });
        }
      } catch (error) {
        console.error('Failed to fetch widget settings:', error);
        // Keep default settings if fetch fails
      }
    };
    fetchSettings();
  }, []);

  // Load Google Analytics only for success page
  useEffect(() => {
    // Inject GA script
    const script1 = document.createElement('script');
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-42R5PVVB4Z';
    script1.async = true;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-42R5PVVB4Z');
    `;
    document.head.appendChild(script2);

    // Cleanup function to remove scripts when component unmounts
    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  if (!state) {
    return null;
  }

  const { email, resources } = state;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Success Icon */}
        <div className="text-center mb-8">
          {/* Logo */}
          {settings.logo_url && (
            <div className="mb-6 flex justify-center">
              <img
                src={settings.logo_url}
                alt={`${settings.school_name} Logo`}
                className="h-24 w-24 object-contain"
              />
            </div>
          )}

          <div
            className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4"
            style={{ backgroundColor: `${settings.accent_color}20` }}
          >
            <svg
              className="w-12 h-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ color: settings.accent_color }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Thank You!
          </h1>
          <p className="text-xl text-gray-600">
            Your information has been received by {settings.school_name}
          </p>
        </div>

        {/* Success Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          {/* What Happens Next */}
          <div className="space-y-4 mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              What Happens Next?
            </h2>

            {resources && resources.length > 0 && (
              <div className="flex items-start">
                <div
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold mr-4"
                  style={{
                    backgroundColor: `${settings.primary_color}20`,
                    color: settings.primary_color
                  }}
                >
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Download Your Resources
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Your personalized guides are ready below - download them now to get started!
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start">
              <div
                className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold mr-4"
                style={{
                  backgroundColor: `${settings.primary_color}20`,
                  color: settings.primary_color
                }}
              >
                {resources && resources.length > 0 ? '2' : '1'}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  We'll Stay In Touch
                </h3>
                <p className="text-gray-600 text-sm">
                  We'll send you more helpful resources and information to <span className="font-medium text-gray-900">{email}</span> as we get to know your family better
                </p>
              </div>
            </div>
          </div>

          {/* Resource Summary (if available) */}
          {resources && resources.length > 0 && (
            <div className="border-t pt-6">
              <h3 className="font-semibold text-gray-900 mb-3">
                🎁 Your Personalized Resources Are Ready!
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Download them now or check your email for the links
              </p>
              <div className="space-y-3">
                {resources.map((resource) => (
                  <div key={resource.id} className="flex items-start p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
                    <svg className="w-5 h-5 text-indigo-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 text-sm mb-1">{resource.title}</p>
                      <p className="text-gray-600 text-xs mb-3">{resource.description}</p>
                      {resource.downloadUrl ? (
                        <a
                          href={resource.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 text-white text-sm font-semibold rounded-lg transition shadow-sm"
                          style={{ backgroundColor: settings.primary_color }}
                          onMouseEnter={(e) => {
                            if (settings.secondary_color) {
                              e.currentTarget.style.backgroundColor = settings.secondary_color;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (settings.primary_color) {
                              e.currentTarget.style.backgroundColor = settings.primary_color;
                            }
                          }}
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download Now
                        </a>
                      ) : (
                        <span className="inline-flex items-center px-4 py-2 bg-gray-300 text-gray-600 text-sm font-medium rounded-lg">
                          Available in email
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Return to Homepage Button */}
        <div className="text-center mt-6">
          <a
            href="https://armentalearningacademy.com"
            className="inline-flex items-center px-6 py-3 text-white font-semibold rounded-lg transition shadow-lg"
            style={{ backgroundColor: settings.primary_color }}
            onMouseEnter={(e) => {
              if (settings.secondary_color) {
                e.currentTarget.style.backgroundColor = settings.secondary_color;
              }
            }}
            onMouseLeave={(e) => {
              if (settings.primary_color) {
                e.currentTarget.style.backgroundColor = settings.primary_color;
              }
            }}
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
            Return to Homepage
          </a>
        </div>
      </div>
    </div>
  );
};

export default EnrollSuccess;
